import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Loader2, QrCode } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
let cache = null;

export const useAdvancedQr = () => {
  const [on, setOn] = useState(cache ?? false);
  useEffect(() => {
    axios.get(`${API}/settings/advanced-qr`).then(r => { cache = r.data.enabled; setOn(cache); }).catch(() => {});
  }, []);
  return on;
};

// Süper Admin: Gelişmiş QR modunu aç/kapat
export const AdvancedQrCard = () => {
  const [on, setOn] = useState(null);
  useEffect(() => { axios.get(`${API}/settings/advanced-qr`).then(r => setOn(r.data.enabled)); }, []);
  const toggle = async (v) => {
    try { await axios.put(`${API}/settings/advanced-qr`, { enabled: v }); cache = v; setOn(v); toast.success(v ? 'Gelişmiş QR açıldı: firmalar personel kimlik ön yüzü yükleyebilir' : 'Gelişmiş QR kapatıldı'); } catch { toast.error('Ayar kaydedilemedi'); }
  };
  return (
    <Card data-testid="advanced-qr-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><QrCode className="h-5 w-5 text-emerald-700" />Gelişmiş QR Doğrulama</CardTitle>
        <CardDescription>Açıkken firmalar talepteki her personel için kimlik kartının <b>ön yüzünü</b> yükler. Güvenlik onay QR'ını okuttuğunda her ismin altında kişinin kimliği görünür ve yüz/isim kontrolü yapılır.</CardDescription>
      </CardHeader>
      <CardContent className="flex items-center gap-3">
        {on === null ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" /> : <Switch checked={on} onCheckedChange={toggle} data-testid="advanced-qr-switch" />}
        <span className={`text-sm font-semibold ${on ? 'text-emerald-700' : 'text-slate-500'}`}>{on ? 'Açık' : 'Kapalı'}</span>
      </CardContent>
    </Card>
  );
};

// Personel satırında kimlik ön yüzü yükleme / görüntüleme
const ID_STATE = {
  onay: ['QR Kimlik Onaylı', 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'],
  eksik: ['QR Kimlik Hatalı', 'border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100'],
  beklemede: ['QR Kimlik Onay Bekliyor', 'border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100'],
};

export const IdCardChip = ({ employee, canEdit, canDecide, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const ref = useRef(null);
  const has = !!employee.id_card_path;
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      await axios.put(`${API}/employees/${employee.id}/id-card`, fd);
      toast.success(`${employee.name} ${employee.surname}: kimlik ön yüzü yüklendi`);
      onChanged?.({ id_card_path: 'uploaded', id_card_status: 'beklemede', id_card_note: '' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Kimlik yüklenemedi'); } finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  const status = employee.id_card_status || 'beklemede';
  const [baseLabel, cls] = has ? ID_STATE[status] || ID_STATE.beklemede : [canEdit ? 'QR Kimlik Yükle' : 'Kimlik Yüklenmedi', 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'];
  const label = employee.nationality === 'yabanci' ? baseLabel.replace('Kimlik', 'Pasaport') : baseLabel;
  const decide = async (st) => {
    const note = st === 'eksik' ? (window.prompt('Hata nedeni (firmaya gösterilir):', 'Kimlikteki isim / T.C. personel kaydıyla uyuşmuyor') || '') : '';
    try {
      await axios.put(`${API}/employees/${employee.id}/id-card/decision`, { status: st, note });
      onChanged?.({ id_card_status: st, id_card_note: note });
      toast.success(st === 'onay' ? 'Kimlik onaylandı' : 'Kimlik hatalı olarak işaretlendi');
      setView(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Karar kaydedilemedi'); }
  };
  const open = () => axios.get(`${API}/employees/${employee.id}/id-card`, { responseType: 'blob' }).then(r => setView(URL.createObjectURL(r.data))).catch(() => toast.error('Kimlik açılamadı'));
  return (
    <>
      <input ref={ref} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      <button type="button" onClick={() => (has ? open() : canEdit && ref.current?.click())} disabled={busy || (!has && !canEdit)}
        className={`inline-flex items-center justify-center gap-1 rounded-md border px-2 py-1 text-[11px] font-semibold leading-tight transition-colors ${cls}`}
        data-testid={`id-card-chip-${employee.id}`} data-id-pending={canDecide && has && status === 'beklemede' ? 'true' : undefined}>
        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CreditCard className="h-3 w-3" />}{label}
      </button>
      {has && status === 'eksik' && employee.id_card_note && <p className="text-[10px] text-rose-600" data-testid={`id-card-note-${employee.id}`}>{employee.id_card_note}</p>}
      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="sm:max-w-lg" data-testid={`id-card-dialog-${employee.id}`}>
          <DialogHeader><DialogTitle>{employee.name} {employee.surname} – {employee.nationality === 'yabanci' ? 'Pasaport Fotoğraflı Sayfa' : 'Kimlik Ön Yüzü'}</DialogTitle></DialogHeader>
          {canDecide && (
            <div className="grid grid-cols-2 gap-2 rounded-lg border bg-slate-50 p-3 text-sm" data-testid={`id-card-record-${employee.id}`}>
              <div><p className="text-xs text-slate-500">Kayıttaki Ad Soyad</p><p className="font-bold text-slate-900">{employee.name} {employee.surname}</p></div>
              <div><p className="text-xs text-slate-500">Kayıttaki T.C. Kimlik No</p><p className="font-bold tabular-nums text-slate-900">{employee.tc_kimlik_no || '-'}</p></div>
              <p className="col-span-2 text-xs text-slate-500">Kimlikteki isim ve T.C. numarası bu bilgilerle aynı mı kontrol edin.</p>
            </div>
          )}
          {view && <img src={view} alt="Kimlik" className="w-full rounded-lg border" />}
          {canDecide && (
            <div className="grid grid-cols-2 gap-2">
              <Button onClick={() => decide('onay')} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`id-card-approve-${employee.id}`}>Bilgiler Uyuşuyor – Onayla</Button>
              <Button variant="outline" onClick={() => decide('eksik')} className="border-rose-300 text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-testid={`id-card-reject-${employee.id}`}>Uyuşmuyor – Hatalı</Button>
            </div>
          )}
          {canEdit && <Button variant="outline" onClick={() => { setView(null); ref.current?.click(); }} className="hover:bg-slate-100 hover:text-slate-900" data-testid={`id-card-replace-${employee.id}`}>Değiştir</Button>}
        </DialogContent>
      </Dialog>
    </>
  );
};
